import { buildQuery } from '@calendly/http-tools'
import { createSelector } from '@reduxjs/toolkit'

import { CALENDAR_ITEMS, CATEGORY_KEYS, SECURITY_AND_COMPLIANCE_ITEMS, VIDEO_CONFERENCING_ITEMS } from './categories'
import { findMatchingIntegrations, SEARCH_DATA_FIELD } from './find-integrations'
import { getCategoryName, integrationName } from './helpers'
import { FIRST_PARTY_INTEGRATIONS_TYPE, INTEGRATION_KEYS, INTEGRATIONS, THIRD_PARTY_INTEGRATIONS_TYPE } from './integration-items'
import { THIRD_PARTY_RECOMMENDATIONS } from './third-party-items'

const sortIntegrationNamesAsc = (a, b) => integrationName(a).localeCompare(integrationName(b))

const listSelector = (state) => state.integrations.list

export const activeTabSelector = createSelector(
  listSelector,
  (list) => (list.activeTab)
)

const categoriesSelector = createSelector(
  listSelector,
  (list) => list.categories
)

const defaultCategorySelector = createSelector(
  listSelector,
  (list) => list.defaultCategory
)

const itemsSelector = createSelector(
  listSelector,
  (list) => (list.items)
)

export const searchTermSelector = createSelector(
  listSelector,
  (list) => (list.searchTerm)
)

export const filterCategorySelector = createSelector(
  listSelector,
  (list) => ({
    filterCategory: list.filterCategory,
    filterCategoryName: getCategoryName(list.filterCategory),
  })
)

export const getBackURL = createSelector(
  filterCategorySelector,
  activeTabSelector,
  ({ filterCategory }, tab) => `/integrations?${buildQuery({ category: filterCategory, tab })}`
)

export const namedCategoriesSelector = createSelector(
  categoriesSelector,
  (categories) => (
    Object.values(categories).map((c) => ({ name: getCategoryName(c.key), ...c }))
  )
)

const allIntegrationsSelector = createSelector(
  itemsSelector,
  (items) => (Object.values(items))
)

export const connectedIntegrationsSelector = createSelector(
  itemsSelector,
  (items) => (Object.values(items).filter((i) => (
    // Filter out the SSO tiles from this list, as they can clutter the view and are only actionable for admins
    i.connected && !SECURITY_AND_COMPLIANCE_ITEMS.includes(i.key)
  )).sort(sortIntegrationNamesAsc))
)

export const integrationCountsSelector = createSelector(
  allIntegrationsSelector,
  connectedIntegrationsSelector,
  (allIntegrations, connectedIntegrations) => ({
    connectedCount: connectedIntegrations.length,
    disconnectedCount: Math.max(allIntegrations.length - connectedIntegrations.length, 0)
  })
)

export const recommendedIntegrationsSelector = createSelector(
  connectedIntegrationsSelector,
  (connectedIntegrations) => {
    const connectedKeys = connectedIntegrations.map((i) => i.key)
    const hasVideoConferencingIntegration = VIDEO_CONFERENCING_ITEMS.some((i) => connectedKeys.includes(i))
    const hasCalendarConferencingIntegration = CALENDAR_ITEMS.some((i) => connectedKeys.includes(i))

    if (hasVideoConferencingIntegration && hasCalendarConferencingIntegration) {
      return []
    }

    if (hasVideoConferencingIntegration) {
      return [
        INTEGRATIONS[INTEGRATION_KEYS.googleCalendar],
        INTEGRATIONS[INTEGRATION_KEYS.exchangeCalendar],
        INTEGRATIONS[INTEGRATION_KEYS.outlookCalendar],
      ]
    }

    if (hasCalendarConferencingIntegration) {
      return [
        INTEGRATIONS[INTEGRATION_KEYS.zoom],
        INTEGRATIONS[INTEGRATION_KEYS.microsoftTeams],
        INTEGRATIONS[INTEGRATION_KEYS.gotomeeting],
      ]
    }

    return [
      INTEGRATIONS[INTEGRATION_KEYS.googleCalendar],
      INTEGRATIONS[INTEGRATION_KEYS.zoom],
      INTEGRATIONS[INTEGRATION_KEYS.microsoftTeams],
    ]
  }
)

export const displayedIntegrationsSelector = createSelector(
  itemsSelector,
  allIntegrationsSelector,
  searchTermSelector,
  filterCategorySelector,
  categoriesSelector,
  defaultCategorySelector,
  (visibleItems, allIntegrations, searchTerm, { filterCategory }, categories, defaultCategory) => {
    if (!searchTerm) {
      const category = categories[filterCategory] || categories[defaultCategory]
      const isUnsortedCategory = category.key === CATEGORY_KEYS.all || category.key === CATEGORY_KEYS.integrations
      const items = category.integrations.map((i) => visibleItems[i])

      if (isUnsortedCategory) {
        items.sort(sortIntegrationNamesAsc)
      }

      return ({
        matches: items,
        matchType: FIRST_PARTY_INTEGRATIONS_TYPE,
      })
    }

    const matches = findMatchingIntegrations(allIntegrations, searchTerm, [SEARCH_DATA_FIELD])

    if (matches.length > 0) {
      return ({
        matches,
        matchType: FIRST_PARTY_INTEGRATIONS_TYPE,
      })
    }

    const thirdPartyMatches = findMatchingIntegrations(THIRD_PARTY_RECOMMENDATIONS, searchTerm, ['name'])

    return ({
      matches: thirdPartyMatches,
      matchType: THIRD_PARTY_INTEGRATIONS_TYPE,
    })
  }
)
